import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  ACCOUNTS_ACTIONS,
  inviteAccount,
  selectAccountsList
} from 'store/accounts';
import { displayAlert } from 'store/v1/ui/ui.actions';
import useModal from 'hooks/useModal';

import { Button, Form, Label } from 'shared';
import Modal from 'modals/layout';
import InviteUsersFormUsers, {
  InviteUsersFormDataUser
} from './InviteUsersFormUsers';
import useEvent from 'v1/helpers/hooks/useEvent';

const copy = {
  title: 'Invite users',
  cancel: 'Cancel',
  submit: 'Invite',
  inviteConflict: 'This user has already been invited'
};

const normalizeEmail = (email: string | number | undefined) =>
  email?.toString().trim().toLowerCase() || '';

export type InviteUsersFormData = {
  users: InviteUsersFormDataUser[];
};

const InviteUsersModal = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const dispatch = useDispatch();
  const accountsList = useSelector(selectAccountsList);
  const { cancelModal, closeModal } = useModal();

  const hasInviteConflict = (email: string | number | undefined) => {
    const normalizedEmail = normalizeEmail(email);
    return accountsList.some(
      account => normalizeEmail(account.email) === normalizedEmail
    );
  };

  useEvent([ACCOUNTS_ACTIONS.inviteAccount], {
    onSuccess: () => {
      dispatch(displayAlert('success', 'Invitations were successfully sent'));
      setIsLoading(false);
      closeModal();
    },
    onFailure: () => {
      setIsLoading(false);
      dispatch(displayAlert('error', 'An error occurred'));
    }
  });

  const onSubmit = ({ users }: InviteUsersFormData) => {
    if (users.some(user => hasInviteConflict(user.email))) {
      dispatch(displayAlert('error', copy.inviteConflict));
      return;
    }

    setIsLoading(true);
    users.forEach(user =>
      dispatch(
        inviteAccount({
          ...user,
          userGroupIds: [user.userGroupId]
        })
      )
    );
  };

  return (
    <Form<InviteUsersFormData> values={{ users: [] }} onSubmit={onSubmit}>
      {methods => (
        <Modal size="s">
          <Modal.Header title={copy.title} />
          <Modal.Content>
            <Modal.Scrollable>
              <div className="stack-s">
                <Label label="Users to invite" size="m" />
              </div>
              <InviteUsersFormUsers hasInviteConflict={hasInviteConflict} />
            </Modal.Scrollable>
            <Modal.Toolbar>
              <Button label={copy.cancel} action={cancelModal} />
              <Button
                isDisabled={
                  methods.watch('users').length < 1 ||
                  methods
                    .watch('users')
                    .some(user => hasInviteConflict(user.email))
                }
                isLoading={isLoading}
                label={`${copy.submit} (${methods.watch('users').length})`}
                icon="tick"
                appearance="primary"
                hasMarginLeft
                action="submit"
              />
            </Modal.Toolbar>
          </Modal.Content>
        </Modal>
      )}
    </Form>
  );
};

export default InviteUsersModal;
